// Core imports
import { useMemo, useState, Fragment, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import Highlighter from 'react-highlight-words';
import moment from 'moment-timezone';

// Component imports
import { Table, Avatar, Space, Tooltip, Typography, Row, Col, Button, Input, Popconfirm, Spin, Tag, Progress, Form, Grid, Modal } from 'antd';
import BotAvatar from '../UI/BotAvatar';
import Prompt from '../UI/Prompt';
import SideModal from '../Layout/SideModal';
import AutomatedAgentTestsSideModalTabs, { AUTOMATED_TEST_TABS } from '../UI/AutomatedAgentTestsSideModalTabs';
import ObservatoryFiltersRow, { useInitialTenantFilters, useObservatoryFilters } from '../UI/ObservatoryFiltersRow';
import AutomatedAgentTestsCloneForm from '../Forms/AutomatedAgentTestsCloneForm';
import { ThunderboltIcon } from '../Icons/ThunderboltIcon';
import { SearchOutlined, HighlightOutlined, DeleteOutlined, LoadingOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { EditIcon } from '../Icons/EditIcon';
import { PlusIcon } from '../Icons/PlusIcon';
import { BotIcon } from '../Icons/BotIcon';

// Misc imports
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { COLOR_STATUS_TYPES, DEFAULT_DATETIME_FORMAT, DEFAULT_DATETIME_TIMEZONE_FORMAT, JOB_TYPES } from '../../constants/main';
import { truncate } from '../../utils/string/truncate';
import { useCreateAutomatedTestsMutation, useDeleteAutomatedTestMutation, useFetchAutomatedTestsQuery, useUpdateAutomatedTestMutation } from '../../store/automatedTests/automatedTestsApi';
import { ListCheckIcon } from '../Icons/ListCheckIcon';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { PLUGIN_CATEGORY } from '../../constants/integrations';
import { useJobPolling } from '../../utils/hooks/useJobPolling';

// Constant declarations
const { useBreakpoint } = Grid;
const PAGE_SIZE = 20;
const ADD_NEW_KEYWORD = 'new';

const RESULT_THRESHOLDS = {
  PASSED: 'passed',
  PASSED_NEEDS_IMPROVEMENT: 'passed_needs_improvement',
  FAILED: 'failed'
};

const AutomatedAgentTestsTable = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [filtersForm] = Form.useForm();
  const formRef = useRef(null);

  const [searchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || PAGE_SIZE, 10);
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const { data: organization, isLoading: isLoadingOrganization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });
  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId, urlParams: { includeBotDeployments: true } }, { skip: !orgId });

  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [isHoveringPollingIconId, setIsHoveringPollingIconId] = useState(null);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [selectedAgentTestToClone, setSelectedAgentTestToClone] = useState({});

  const { showModalConfirm } = useModalConfirm(formIsDirty);
  const [deleteAutomatedTest, { isLoading: isDeletingTest }] = useDeleteAutomatedTestMutation();

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const observatoryFilters = useObservatoryFilters(orgTimezone);
  const initialTenantFilters = useInitialTenantFilters();

  const { isLoading: isCreatingTest } = useCreateAutomatedTestsMutation()[1];
  const { isLoading: isUpdatingTest } = useUpdateAutomatedTestMutation()[1];

  const { data: automatedTests, isFetching: isFetchingTests, refetch: refetchAutomatedTests } = useFetchAutomatedTestsQuery({
    orgId,
    urlParams: {
      page: currentPage - 1,
      pageSize,
      sortBy: searchParams.get('sortBy') || 'createdAt',
      order: searchParams.get('order') || 'desc',
      ...(searchParams.get('search') && { searchText: searchParams.get('search') }),
      ...((JSON.parse(decodeURIComponent(searchParams.get('tenantsFilter'))) || [])?.length !== 0 && { examinedTenantIds: JSON.parse(decodeURIComponent(searchParams.get('tenantsFilter')))?.map(item => item.tenant) }),
      ...(searchParams.get('successRateGte') && { successRateGte: searchParams.get('successRateGte') }),
      ...(searchParams.get('successRateLte') && { successRateLte: searchParams.get('successRateLte') })
    }
  }, {
    skip: !orgId,
    selectFromResult: (result) => ({
      ...result,
      data: {
        ...result.data,
        itemsMap: new Map(result.data?.items?.map(item => [item.id, item]) || [])
      }
    })
  });

  const {
    state: jobPollingState,
    actions: jobPollingActions
  } = useJobPolling({
    orgId,
    type: JOB_TYPES.AUTO_BOT_TEST,
    onJobFinished: () => {
      refetchAutomatedTests();
    }
  });

  const handleShowConfirm = (defaultAction) => {
    showModalConfirm({
      defaultAction: () => {
        setFormIsDirty(false);
        defaultAction();
      },
      onConfirm: async () => {
        const success = await formRef.current?.submit();
        if (success) {
          setFormIsDirty(false);
          defaultAction();
        }
      },
      okText: t('Save Changes'),
      cancelText: t('Quit without saving'),
      title: t('You have unsaved changes'),
      content: t('Do you want to save them before proceeding?')
    });
  };

  const handleDeleteTest = async (testId) => {
    if (searchParams.get('testId') === testId) {
      handleCloseSideModal();
    }
    await deleteAutomatedTest({ orgId, id: testId }).unwrap();
  };

  const debouncedUpdateUrl = useDebouncedCallback((value) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    if (value) {
      urlSearchParams.set('search', value);
    } else {
      urlSearchParams.delete('search');
    }
    urlSearchParams.set('page', '1');
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  }, 300);

  // const activeResultFilter = useMemo(() => {
  //   const successRateGte = searchParams.get('successRateGte');
  //   const successRateLte = searchParams.get('successRateLte');

  //   if (successRateGte === '80' && !successRateLte) {
  //     return RESULT_THRESHOLDS.PASSED;
  //   }

  //   if (successRateGte === '70' && successRateLte === '79') {
  //     return RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT;
  //   }

  //   if (!successRateGte && successRateLte === '69') {
  //     return RESULT_THRESHOLDS.FAILED;
  //   }

  //   return null;
  // }, [searchParams]);

  const activeAutomatedTest = useMemo(() => {
    const testId = searchParams.get('testId');
    if (!testId) {
      return null;
    }
    if (testId === ADD_NEW_KEYWORD) {
      return { isCreateMode: true };
    }

    return automatedTests?.items?.find(test => test.id === testId) || null;
  }, [searchParams, automatedTests?.items]);

  const handleOpenModal = (record, tab) => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('testId', record.id);
    urlSearchParams.set('tab', tab);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleAddNewAutomatedTest = () => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.set('testId', ADD_NEW_KEYWORD);
    urlSearchParams.set('tab', AUTOMATED_TEST_TABS.DETAILS);
    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedUpdateUrl(value);
  };

  const handleCloseSideModal = () => {
    const urlSearchParams = new URLSearchParams(searchParams);
    urlSearchParams.delete('testId');
    urlSearchParams.delete('tab');
    setTimeout(() => {
      navigate({ search: urlSearchParams.toString() }, { replace: true });
    }, 100);
  };

  const openCloneModal = (record) => {
    setIsCloneModalOpen(true);
    setSelectedAgentTestToClone(record);
  };
  const closeCloneModal = () => {
    setIsCloneModalOpen(false);
    setSelectedAgentTestToClone({});
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const urlSearchParams = new URLSearchParams(searchParams);

    urlSearchParams.set('page', pagination.current);
    urlSearchParams.set('pageSize', pagination.pageSize);

    if (sorter.field && sorter.order) {
      urlSearchParams.set('sortBy', sorter.field);
      urlSearchParams.set('order', sorter.order === 'ascend' ? 'asc' : 'desc');
    } else {
      urlSearchParams.set('sortBy', 'createdAt');
      urlSearchParams.set('order', 'desc');
    }

    if (filters.examinedTenantId) {
      urlSearchParams.set('agents', filters.examinedTenantId.join(','));
    } else {
      urlSearchParams.delete('agents');
    }

    urlSearchParams.delete('successRateGte');
    urlSearchParams.delete('successRateLte');

    if (filters.lastResultSuccessRate && filters.lastResultSuccessRate.length > 0) {
      const filterValue = filters.lastResultSuccessRate[0];
      switch (filterValue) {
        case RESULT_THRESHOLDS.PASSED:
          urlSearchParams.set('successRateGte', 80);
          break;
        case RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT:
          urlSearchParams.set('successRateGte', 70);
          urlSearchParams.set('successRateLte', 79);
          break;
        case RESULT_THRESHOLDS.FAILED:
          urlSearchParams.set('successRateLte', 69);
          break;
        default:
          break;
      }
    }

    navigate({ search: urlSearchParams.toString() }, { replace: true });
  };

  const sortingInfo = useMemo(() => {
    const order = searchParams.get('order') ? (searchParams.get('order') === 'desc' ? 'descend' : 'ascend') : 'descend';
    const columnKey = searchParams.get('sortBy') || 'createdAt';
    return { order, columnKey };
  }, [searchParams.get('sortBy'), searchParams.get('order')]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record) => ({
      disabled: jobPollingState.pollingJobsByObjectIdMap.has(record.id),
      name: record.name
    }),
    selections: [
      {
        key: 'all',
        text: t('All'),
        onSelect: () => {
          const selectableKeys = automatedTests?.items
            ?.filter(item => !jobPollingState.pollingJobsByObjectIdMap.has(item.id))
            .map(item => item.id);
          setSelectedRowKeys(selectableKeys || []);
        }
      },
      {
        key: 'none',
        text: t('None'),
        onSelect: () => {
          setSelectedRowKeys([]);
        }
      }
    ]
  };

  const columns = [
    {
      title: t('Test Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'name' && sortingInfo.order,
      render: (text, record) => {
        const currentSearch = (searchParams.get('search') || '').toLowerCase();

        return (
          <Space>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchTerm]}
              autoEscape
              textToHighlight={text ? text.toString() : ''}
            />
            {
              currentSearch && !record.name?.toLowerCase().includes(currentSearch) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('Match found in description')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        );
      }
    },
    {
      title: t('Agent', { context: 'AI agent' }),
      dataIndex: 'examinedTenantId',
      key: 'examinedTenantId',
      filterMultiple: true,
      filteredValue: searchParams.get('agents')?.split(',') || null,
      filterSearch: (input, record) => {
        const bot = (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING))?.find((bot) => bot.id === record.value);
        return bot?.displayName.toLowerCase().includes(input.toLowerCase());
      },
      filters: (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING))?.map(bot => ({
        text: (
          <Space size={4} align="center" wrap={false}>
            <Avatar src={bot?.image} size={24} shape="square" className="mr-2">
              <BotIcon />
            </Avatar>
            <Tooltip title={bot?.displayName?.length >= 50 && bot?.displayName}>
              <Typography.Text ellipsis>{truncate(bot.displayName, 50, '...')}</Typography.Text>
            </Tooltip>
          </Space>
        ),
        value: bot.id
      })),
      render: (tenantId) => {
        const bot = bots?.items?.find(b => b.id === tenantId);
        return bot ? <BotAvatar avatarSize={24} bot={bot} /> : tenantId;
      }
    },
    {
      title: t('Created at'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'createdAt' && sortingInfo.order,
      render: (date) => (date ? moment(date).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : '-')
    },
    {
      title: t('Last Run'),
      dataIndex: 'testRunDate',
      key: 'testRunDate',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'testRunDate' && sortingInfo.order,
      render: (date) => (date ? moment(date).tz(organization?.timezone).format(DEFAULT_DATETIME_FORMAT) : 'N/A')
    },
    {
      title: t('Sessions Passed'),
      dataIndex: 'lastResultSuccessRate',
      key: 'lastResultSuccessRate',
      align: 'center',
      sorter: true,
      sortOrder: sortingInfo.columnKey === 'lastResultSuccessRate' && sortingInfo.order,
      // filteredValue: activeResultFilter ? [activeResultFilter] : null,
      // filterMultiple: false,
      // filters: [
      //   { text: t('Passed (>=80%)'), value: RESULT_THRESHOLDS.PASSED },
      //   { text: t('Passed (70-79%)'), value: RESULT_THRESHOLDS.PASSED_NEEDS_IMPROVEMENT },
      //   { text: t('Failed (<70%)'), value: RESULT_THRESHOLDS.FAILED }
      // ],
      render: (rate, record) => {
        if (!record.testRunDate) {
          return '-';
        }

        let color;
        if (rate >= 80) {
          color = COLOR_STATUS_TYPES.SUCCESS;
        } else if (rate >= 70) {
          color = COLOR_STATUS_TYPES.WARNING;
        } else {
          color = COLOR_STATUS_TYPES.ERROR;
        }

        const formattedRate = rate % 1 === 0 ? rate : rate.toFixed(2);
        return <Tag className="fw-600 py-1 px-3" color={color}>{`${formattedRate}%`}</Tag>;
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 240,
      align: 'center',
      render: (record) => {
        const isRunning = jobPollingState.pollingJobsByObjectIdMap.has(record.id);
        return (
          <Space onClick={(e) => e.stopPropagation()}>
            <Tooltip
              title={
                isRunning ? (
                  <Space direction="vertical" className="w-100 d-flex justify-content-center align-items-center">
                    <Typography.Text strong>
                      {t('Progress:')}
                    </Typography.Text>
                    <Progress percent={jobPollingState?.pollingJobsByObjectIdMap?.get(record.id)?.progress} type="circle" />
                  </Space>
                ) : (
                  <Typography.Text>
                    {t('Run Test')}
                  </Typography.Text>
                )
              }
            >
              <Button
                shape="circle"
                type="text"
                onMouseEnter={() => {
                  setIsHoveringPollingIconId(record.id);
                }}
                onMouseLeave={() => {
                  setIsHoveringPollingIconId(null);
                }}
                icon={isRunning
                  ? isHoveringPollingIconId === record.id
                    ? <CloseOutlined />
                    : <LoadingOutlined spin />
                  : <ThunderboltIcon />
                }
                onClick={async () => {
                  if(isRunning && isHoveringPollingIconId === record.id) {
                    if(jobPollingState?.pollingJobsByObjectIdMap?.has(record.id)) {
                      const jobIdToCancel = jobPollingState?.pollingJobsByObjectIdMap?.get(record.id)?.id;
                      await jobPollingActions.cancelJob(jobIdToCancel);
                      setIsHoveringPollingIconId(null);
                    }
                    return;
                  }
                  await handleOnTestRun(record.id);
                }}
              />
            </Tooltip>

            <Tooltip title={t('View Results')}>
              <Button
                shape="circle"
                type="text"
                icon={<ListCheckIcon />}
                onClick={() => handleOpenModal(record, AUTOMATED_TEST_TABS.RESULTS)}
              />
            </Tooltip>

            <Tooltip title={t('Edit Test')}>
              <Button
                shape="circle"
                type="text"
                icon={<EditIcon />}
                onClick={() => handleOpenModal(record, AUTOMATED_TEST_TABS.DETAILS)}
              />
            </Tooltip>

            <Tooltip overlayClassName="pointer-events-none" title={t('Clone Test')}>
              <Button
                shape="circle"
                type="text"
                icon={<CopyOutlined />}
                onClick={() => openCloneModal(record)}
              />
            </Tooltip>

            <Popconfirm
              title={t('Delete this test?')}
              description={t('This action cannot be undone.')}
              onConfirm={() => handleDeleteTest(record.id)}
              okText={t('Delete')}
              cancelText={t('Cancel')}
              placement="topRight"
            >
              <Tooltip title={t('Delete Test')}>
                <Button
                  shape="circle"
                  type="text"
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const handleOnFormSubmit = async () => {
    if (formRef.current) {
      const result = await formRef.current.submit();

      if (result && result.id) {
        setFormIsDirty(false);
        handleCloseSideModal();
        return result.id;
      }
    }
    return null;
  };

  const handleOnTestRun = async (automatedTestIdToRun) => {
    if (!automatedTestIdToRun) {
      return;
    }
    await jobPollingActions.createAndTrackJobs([
      {
        objectId: automatedTestIdToRun,
        type: JOB_TYPES.AUTO_BOT_TEST,
        description: `${automatedTests?.itemsMap?.get(automatedTestIdToRun)?.name}`
      }
    ]);
  };

  const handleBulkRun = async () => {
    const jobsToCreate = selectedRowKeys.map(testId => ({
      objectId: testId,
      type: JOB_TYPES.AUTO_BOT_TEST,
      description: `${automatedTests?.itemsMap?.get(testId)?.name}`
    }));
    await jobPollingActions.createAndTrackJobs(jobsToCreate);
    setSelectedRowKeys([]);
  };

  const handleOnSubmitAndRun = async () => {
    const automatedTestId = await handleOnFormSubmit();
    await handleOnTestRun(automatedTestId);
  };

  const isCurrentTestInSideModalRunning = !!activeAutomatedTest && jobPollingState.pollingJobsByObjectIdMap.has(activeAutomatedTest.id);

  const isLoading = isLoadingOrganization || isFetchingTests || isFetchingBots || isDeletingTest;

  if (isLoadingOrganization || !organization) {
    return (
      <Row className="d-flex justify-content-center align-items-center">
        <Col >
          <Spin />
        </Col>
      </Row>
    );
  }

  return (
    <Fragment>
      <Prompt
        when={formIsDirty}
        message={t('You have unsaved changes. Are you sure you want to leave?')}
      />

      <Form
        layout={isMobileOrTabletSize ? 'vertical' : 'inline'}
        name="surveyFiltersForm"
        initialValues={{
          tenantsFilter: initialTenantFilters,
          rangePicker: [observatoryFilters.startDate, observatoryFilters.endDate]
        }}
        form={filtersForm}
      >
        <div className="w-100">
          <ObservatoryFiltersRow
            form={filtersForm}
            rangePickerProps={{
              disabled: true,
              timezone: orgTimezone
            }}
            agentsPickerProps={{
              agentsList: (bots?.items || []).filter(bot => bot.plugins.some(plugin => plugin.category === PLUGIN_CATEGORY.LLM_AUTOMATED_AGENT_TESTING)) || [],
              disabled: isFetchingTests || isLoading
            }}
          />
        </div>

        <Row gutter={24} className="mb-2">
          <Col flex="auto">
            <Space className="flex-wrap">
              <Input
                size="large"
                placeholder={t('Search in contents')}
                prefix={<SearchOutlined className="text-secondary" />}
                allowClear
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Space>
          </Col>

          <Col>
            <Space>
              <Button
                block
                size="large"
                onClick={handleBulkRun}
                disabled={selectedRowKeys.length === 0}
              >
                {t('Bulk Run')}
              </Button>

              <Button
                icon={<PlusIcon />}
                type="primary"
                className="float-right"
                size="large"
                onClick={handleAddNewAutomatedTest}
                loading={isCreatingTest}
              >
                {t('Add Test')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      <Row gutter={24}>
        <Col xs={24}>
          <Table
            dataSource={automatedTests?.items}
            columns={columns}
            rowSelection={rowSelection}
            scroll={{ y: 680 }}
            className="scroll-table"
            rowKey="id"
            onRow={(record) => ({
              onClick: () => handleOpenModal(record, AUTOMATED_TEST_TABS.DETAILS)
            })}
            loading={isLoading}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: automatedTests?.total || 0,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Tests')}: ${total}`
            }}
          />
        </Col>
      </Row>

      <SideModal
        open={!!activeAutomatedTest}
        onClose={() => handleShowConfirm(handleCloseSideModal)}
        title={activeAutomatedTest?.isCreateMode ? t('Create New Test') : `${t('Edit')} ${activeAutomatedTest?.name}`}
        bodyClassName="p-0"
        destroyOnClose
        width={900}
        hasFullScreenButton
      >
        <div className="px-6 pt-6 flex-grow-1">
          <AutomatedAgentTestsSideModalTabs
            activeAutomatedTest={activeAutomatedTest}
            formRef={formRef}
            onFormChange={setFormIsDirty}
            handleShowConfirm={handleShowConfirm}
          />
        </div>

        <div className="ant-drawer-footer">
          <Space className="d-flex justify-content-between">
            <Typography.Paragraph
              ellipsis
              className={`${activeAutomatedTest?.isCreateMode && 'opacity-0'} text-secondary fs-sm mb-0`}
            >
              {
                !activeAutomatedTest?.isCreateMode ? (
                  <Fragment>
                    <span className="fw-bold">{t('Created')}:</span> {moment(activeAutomatedTest?.createdAt).tz(organization?.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                    <span className="fw-bold">{t('Last Run')}:</span> {moment(activeAutomatedTest?.testRunDate).tz(organization?.timezone).format(DEFAULT_DATETIME_TIMEZONE_FORMAT)}<br/>
                  </Fragment>
                ) : null
              }
            </Typography.Paragraph>

            <Row justify="end" gutter={8} hidden={searchParams.get('tab') === AUTOMATED_TEST_TABS.RESULTS}>
              <Col>
                <Button
                  size="large"
                  onClick={handleCloseSideModal}
                  disabled={isCreatingTest || isUpdatingTest}
                >
                  {t('Cancel')}
                </Button>
              </Col>

              <Col>
                <Button
                  loading={isCreatingTest || isUpdatingTest}
                  disabled={isCreatingTest || isUpdatingTest}
                  className="float-right"
                  size="large"
                  onClick={handleOnFormSubmit}
                >
                  {activeAutomatedTest?.isCreateMode ? t('Create Test') : t('Save Changes')}
                </Button>
              </Col>

              <Col>
                <Button
                  loading={isCreatingTest || isUpdatingTest || isCurrentTestInSideModalRunning}
                  disabled={isCreatingTest || isUpdatingTest || isCurrentTestInSideModalRunning}
                  className="float-right"
                  type="primary"
                  size="large"
                  onClick={handleOnSubmitAndRun}
                >
                  {activeAutomatedTest?.isCreateMode ? t('Create & Run') : t('Save & Run')}
                </Button>
              </Col>
            </Row>
          </Space>
        </div>
      </SideModal>

      <Modal
        open={isCloneModalOpen}
        onCancel={closeCloneModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        className="ant-modal-p-0"
        centered
        closable={false}
      >
        <Row className="bg-layout-gray border-radius-top-left-base border-radius-top-right-base p-4">
          <Col xs={20} className="d-flex align-items-center">
            <Typography.Title level={3} className="mb-0">
              {t('Clone Test')}
            </Typography.Title>
          </Col>
          <Col xs={4} className="mt-2">
            <CloseOutlined className="float-right fs-xl cursor-pointer" onClick={() => {
              closeCloneModal();
            }}
            />
          </Col>
        </Row>
        <Row gutter={24} justify="center" className="p-4 border-radius-bottom-left-base border-radius-bottom-right-base">
          <Col xs={24}>
            <Typography.Text className="text-muted">
              {t('Cloned Test Details:')}
            </Typography.Text>
          </Col>
          <Col xs={24}>
            <AutomatedAgentTestsCloneForm
              agentTestToClone={selectedAgentTestToClone}
              onSuccess={closeCloneModal}
              onCancel={closeCloneModal}
            />
          </Col>
        </Row>
      </Modal>

    </Fragment>
  );
};

export default AutomatedAgentTestsTable;
